<template>
  <div class="view-banner" :class="{ 'view-banner--exit': exit }" :style="bannerStyle">
    <div class="view-banner__copy">
      <span class="view-banner__icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5L3 8z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
        </svg>
      </span>
      <div>
        <span v-if="title" class="view-banner__title">{{ title }}</span>
        <span v-if="body" class="view-banner__body">{{ body }}</span>
      </div>
    </div>
    <a :href="href" class="view-banner__cta">{{ cta }} →</a>
  </div>
</template>

<script setup lang="ts">
/**
 * Bar offering a storefront view (BOAT-1912) — "Looking for an official match
 * break package? → Explore" — or, with `exit`, the way back to the full page.
 * The offer bar takes the view's Key Feature Label colour; the exit bar stays
 * neutral. Text flips to black on light colours (same rule as KeyLabelFeature).
 */
const props = defineProps<{
  title?: string | null
  body?: string | null
  cta: string
  href: string
  exit?: boolean
  colour?: string | null
}>()

const isDark = (hex: string) => {
  let c = hex.replace('#', '')
  if (c.length === 3) c = c.split('').map((x) => x + x).join('')
  if (!/^[0-9a-f]{6}$/i.test(c)) return true
  const r = parseInt(c.slice(0, 2), 16)
  const g = parseInt(c.slice(2, 4), 16)
  const b = parseInt(c.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 < 150
}

const bannerStyle = computed(() => {
  if (props.exit || !props.colour) return undefined
  const dark = isDark(props.colour)
  return {
    '--view-banner-bg': props.colour,
    '--view-banner-fg': dark ? '#ffffff' : '#121318',
    '--view-banner-cta-bg': dark ? '#ffffff' : '#121318',
    '--view-banner-cta-fg': dark ? '#121318' : '#ffffff',
    '--view-banner-icon-bg': dark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(18, 19, 24, 0.08)',
  }
})
</script>

<style scoped lang="scss">
@import '@boats/ui/styles/config';

.view-banner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  background: var(--view-banner-bg, #121318);
  color: var(--view-banner-fg, #{$white});

  @include media-breakpoint-up(md) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 16px 24px;
  }

  &--exit {
    background: $gray-200;
    color: #121318;

    .view-banner__cta {
      background: #121318;
      color: $white;
    }
  }

  &__copy {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    & > div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 2px;
    }
  }

  &__icon {
    display: inline-flex;
    flex: none;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--view-banner-icon-bg, rgba(255, 255, 255, 0.12));

    .view-banner--exit & {
      background: rgba(18, 19, 24, 0.08);
    }
  }

  &__title {
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
  }

  &__body {
    font-size: 13px;
    line-height: 18px;
    opacity: 0.85;
  }

  &__cta {
    @include smooth-transition;
    flex: none;
    align-self: flex-start;
    padding: 10px 18px;
    border-radius: 6px;
    background: var(--view-banner-cta-bg, #{$white});
    color: var(--view-banner-cta-fg, #121318);
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-decoration: none;
    white-space: nowrap;

    @include media-breakpoint-up(md) {
      align-self: center;
    }

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
